import { useEffect } from 'react';

interface SeoProps {
  title: string;
  description: string;
  keywords?: string;
  author?: string;
  schema?: string;
  schemas?: string[];
  // Ignored: the canonical URL is set for every route by <Canonical /> (see routes).
  canonical?: string;
}

const SCHEMA_SCRIPT_ID_PREFIX = 'seo-ld-json';

const Seo: React.FC<SeoProps> = ({ title, description, keywords, author, schema, schemas }) => {
  useEffect(() => {
    document.title = title;

    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', description);

    if (keywords) {
      let metaKeywords = document.querySelector('meta[name="keywords"]');
      if (!metaKeywords) {
        metaKeywords = document.createElement('meta');
        metaKeywords.setAttribute('name', 'keywords');
        document.head.appendChild(metaKeywords);
      }
      metaKeywords.setAttribute('content', keywords);
    }
  }, [title, description, keywords]);

  // Public pages using <Seo> are indexable; the tags are removed on leaving the page so they
  // never linger on private/app routes that don't render <Seo>.
  useEffect(() => {
    const added: HTMLMetaElement[] = [];
    const setMeta = (name: string, content: string) => {
      let tag = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('name', name);
        document.head.appendChild(tag);
        added.push(tag);
      }
      tag.setAttribute('content', content);
    };

    setMeta('robots', 'index, follow, max-image-preview:large');
    if (author) setMeta('author', author);

    return () => {
      added.forEach((tag) => tag.remove());
    };
  }, [author]);

  useEffect(() => {
    const allSchemas = [...(schema ? [schema] : []), ...(schemas || [])];

    document.querySelectorAll(`script[id^="${SCHEMA_SCRIPT_ID_PREFIX}-"]`).forEach((el) => el.remove());

    const added: HTMLScriptElement[] = [];
    allSchemas.forEach((s, index) => {
      if (!s || !s.trim()) return;
      try {
        JSON.parse(s);
      } catch {
        return;
      }

      const script = document.createElement('script');
      script.id = `${SCHEMA_SCRIPT_ID_PREFIX}-${index}`;
      script.type = 'application/ld+json';
      script.text = s;
      document.head.appendChild(script);
      added.push(script);
    });

    return () => {
      added.forEach((script) => script.remove());
    };
  }, [schema, schemas]);

  return null;
};

export default Seo;
